import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';

interface SearchableSelectProps {
  options: string[];
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  emptyLabel?: string;
  className?: string;
  disabled?: boolean;
  /** Free-text mode: the trigger is a text input and a typed value is kept even when it is not one of `options`. */
  allowCustom?: boolean;
}

// The option panel is portaled to <body> and positioned in viewport
// coordinates, so an `overflow: hidden` ancestor (e.g. the Comparison page's
// Advanced Filters drawer) can never clip it.
const MENU_MAX_HEIGHT = 288; // Tailwind max-h-72
const MENU_GAP = 4;          // Tailwind mt-1
const VIEWPORT_MARGIN = 8;
const MIN_MENU_HEIGHT = 96;

interface MenuPosition {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  emptyLabel,
  className = '',
  disabled = false,
  allowCustom = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [customQuery, setCustomQuery] = useState('');
  const [highlight, setHighlight] = useState(-1);
  const [pos, setPos] = useState<MenuPosition | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const listboxId = useId();
  const skipFocusOpen = useRef(false);

  // Free-text mode filters by what the user typed; select mode filters by the
  // search box inside the panel.
  const query = allowCustom ? customQuery : search;
  const filtered = query
    ? options.filter((o) => o.toLowerCase().includes(query.toLowerCase()))
    : options;
  const exactMatch = options.some((o) => o === value);

  // Measure the trigger and derive the panel geometry. Flips above the
  // trigger when the viewport has less room below than above.
  const updatePosition = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return; // not measurable
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const spaceBelow = vh - rect.bottom - MENU_GAP - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - MENU_GAP - VIEWPORT_MARGIN;
    const flip = spaceBelow < MENU_MAX_HEIGHT && spaceAbove > spaceBelow;
    const maxHeight = Math.max(
      MIN_MENU_HEIGHT,
      Math.min(MENU_MAX_HEIGHT, flip ? spaceAbove : spaceBelow),
    );
    const left = Math.min(
      Math.max(VIEWPORT_MARGIN, rect.left),
      Math.max(VIEWPORT_MARGIN, vw - rect.width - VIEWPORT_MARGIN),
    );
    const top = flip ? rect.top - MENU_GAP - maxHeight : rect.bottom + MENU_GAP;
    setPos((prev) =>
      prev &&
      prev.top === top &&
      prev.left === left &&
      prev.width === rect.width &&
      prev.maxHeight === maxHeight
        ? prev
        : { top, left, width: rect.width, maxHeight },
    );
  }, []);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setSearch('');
    setCustomQuery('');
    setHighlight(-1);
  }, []);

  const openMenu = useCallback(() => {
    if (disabled) return;
    // Opening free-text mode shows every option - a prefilled value must not
    // narrow the list down before the user has typed anything.
    if (allowCustom) setCustomQuery('');
    updatePosition();
    setOpen(true);
  }, [disabled, updatePosition, allowCustom]);

  // Returning focus to the input after a pick must not reopen the menu, so the
  // focus event that comes with it is swallowed once.
  const focusInput = useCallback(() => {
    const el = inputRef.current;
    if (!el || document.activeElement === el) return;
    skipFocusOpen.current = true;
    el.focus({ preventScroll: true });
    window.setTimeout(() => { skipFocusOpen.current = false; }, 0);
  }, []);

  const onInputFocus = useCallback(() => {
    if (skipFocusOpen.current) {
      skipFocusOpen.current = false;
      return;
    }
    openMenu();
  }, [openMenu]);

  const select = useCallback(
    (v: string) => {
      onChange(v);
      closeMenu();
      if (allowCustom) focusInput();
    },
    [onChange, closeMenu, allowCustom, focusInput],
  );

  // Keep the panel glued to the trigger. A rAF loop is used instead of
  // scroll/resize listeners because the trigger can also move when the page
  // reflows or the scroll offset is clamped by a document height change —
  // neither of which reliably emits a scroll event, which would leave the
  // panel drifting away from its trigger.
  useEffect(() => {
    if (!open) return;
    let frame = 0;
    const sync = () => {
      updatePosition();
      frame = window.requestAnimationFrame(sync);
    };
    frame = window.requestAnimationFrame(sync);
    return () => window.cancelAnimationFrame(frame);
  }, [open, updatePosition]);

  // Close on an outside mousedown. The panel lives in a portal, so it must be
  // treated as "inside" explicitly — otherwise the mousedown would close the
  // menu before the option's click handler ever runs.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      closeMenu();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open, closeMenu]);

  // Escape closes the menu and returns focus to the trigger.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      closeMenu();
      if (allowCustom) focusInput();
      else btnRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, closeMenu, allowCustom, focusInput]);

  // Focus the filter input without scrolling the page (the panel is fixed).
  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus({ preventScroll: true });
  }, [open]);

  // A parent that hides this control (e.g. the Advanced Filters drawer
  // collapsing) must not leave an orphaned panel floating over the page.
  useEffect(() => {
    if (disabled && open) closeMenu();
  }, [disabled, open, closeMenu]);

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) {
        openMenu();
        setHighlight(0);
      } else {
        setHighlight((h) => (filtered.length ? Math.min(h + 1, filtered.length - 1) : -1));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && highlight >= 0 && filtered[highlight]) select(filtered[highlight]);
      else closeMenu();
    }
  };

  const display = value || (emptyLabel ?? placeholder ?? '');

  return (
    <div ref={ref} className={`relative ${className}`}>
      {allowCustom ? (
        <>
          <input
            ref={inputRef}
            type="text"
            value={value}
            disabled={disabled}
            placeholder={placeholder}
            role="combobox"
            aria-expanded={open}
            aria-controls={open ? listboxId : undefined}
            aria-autocomplete="list"
            onChange={(e) => {
              onChange(e.target.value);
              setCustomQuery(e.target.value);
              setHighlight(-1);
              setOpen(true);
            }}
            onFocus={onInputFocus}
            onKeyDown={onInputKeyDown}
            className="w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2 sm:py-2.5 pr-9 text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
          />
          <button
            type="button"
            disabled={disabled}
            aria-label="Toggle options"
            onClick={() => (open ? closeMenu() : openMenu())}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface transition-colors disabled:cursor-not-allowed disabled:opacity-60"
          >
            <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </>
      ) : (
        <button
          ref={btnRef}
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          onClick={() => (open ? closeMenu() : openMenu())}
          className={`bg-surface-lowest border border-border-subtle rounded px-3 py-1.5 text-body-sm text-left focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer w-full flex items-center justify-between gap-2 disabled:cursor-not-allowed disabled:opacity-60 ${value ? 'text-on-surface' : 'text-on-surface-variant'}`}
        >
          <span className="truncate">{display}</span>
          <svg className={`w-3 h-3 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}
      {open && pos && typeof document !== 'undefined' && createPortal(
        <div
          ref={panelRef}
          style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
          className="fixed z-[100] bg-surface border border-border-subtle rounded shadow-lg flex flex-col overflow-hidden"
        >
          {!allowCustom && (
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={placeholder ?? 'Search...'}
              className="bg-surface-lowest border-b border-border-subtle px-3 py-2 text-body-sm text-on-surface placeholder:text-on-surface-variant/50 outline-none focus:ring-2 focus:ring-primary/20"
            />
          )}
          <div id={listboxId} role="listbox" aria-label={placeholder ?? 'Options'} className="overflow-y-auto flex-1">
            {allowCustom && value.trim() !== '' && !exactMatch && (
              <button
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => select(value)}
                className="w-full text-left px-3 py-1.5 text-body-sm text-primary font-semibold hover:bg-primary/10"
              >
                {`Use "${value}"`}
              </button>
            )}
            {!allowCustom && emptyLabel && (
              <button
                type="button"
                role="option"
                aria-selected={!value}
                onClick={() => select('')}
                className={`w-full text-left px-3 py-1.5 text-body-sm hover:bg-primary/10 ${!value ? 'text-primary font-semibold' : 'text-on-surface-variant'}`}
              >
                {emptyLabel}
              </button>
            )}
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-body-sm text-on-surface-variant/60">No matches</div>
            ) : (
              filtered.map((o, i) => (
                <button
                  key={o}
                  type="button"
                  role="option"
                  aria-selected={value === o}
                  onMouseEnter={allowCustom ? () => setHighlight(i) : undefined}
                  onClick={() => select(o)}
                  className={`w-full text-left px-3 py-1.5 text-body-sm ${allowCustom && highlight === i ? 'bg-primary/10' : 'hover:bg-primary/10'} ${value === o ? 'text-primary font-semibold' : 'text-on-surface'}`}
                >
                  {o}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SearchableSelect;
